import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAssistantPanel } from '@/contexts/AssistantContext';
import { assistantSuggestions } from '@/lib/ai-assistant/assistantSuggestions';
import { isEditorPath } from '@/lib/ai-assistant/clientTools';

/** Snapshot on a new chat/page/clip, not on every playhead tick or draft edit. */
export function useAssistantSuggestions(active: boolean) {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const locale = (i18n?.resolvedLanguage || i18n?.language || 'pt').startsWith('en') ? 'en' : 'pt';
  const { suggestionSession, pageBridgeKey, getSuggestions } = useAssistantPanel();
  // The query string stays out: only the path reaches the backend, and page
  // filters in the URL (e.g. /analytics) must not regenerate starters. A clip
  // switch in the editor changes pageBridgeKey instead.
  const key = JSON.stringify([suggestionSession, location.pathname, pageBridgeKey, locale]);
  const [result, setResult] = useState<{ key: string; texts: string[] } | null>(null);
  const waitingForEditor = isEditorPath(location.pathname) && !pageBridgeKey;

  useEffect(() => {
    if (!active || waitingForEditor) return undefined;
    let cancelled = false;
    getSuggestions(key, locale).then((texts) => {
      if (!cancelled) setResult({ key, texts });
    }).catch(() => {
      if (!cancelled) setResult({ key, texts: [] });
    });
    return () => { cancelled = true; };
  }, [active, waitingForEditor, key, locale, getSuggestions]);

  const current = result?.key === key ? result : null;
  return {
    loading: active && (waitingForEditor || !current),
    suggestions: current?.texts.length
      ? current.texts
      : assistantSuggestions(location.pathname).map((name) => t(`aiAssistant.suggestions.${name}`)),
  };
}
